import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["instructionInput", "whisperInput", "speakInput", "speakStatus", "instructSection", "whisperSection", "speakSection", "instructButton", "whisperButton", "speakButton", "sentimentBanner", "sentimentDot", "sentimentHeadline", "sentimentDetail"]

  connect() {
    console.log("Conversation monitor controller connected")
    this.currentMode = 'instruct' // Default to instruct mode

    // Wait for DOM to be fully loaded, then scroll
    setTimeout(() => {
      this.scrollToBottom('auto')
    }, 100)

    // Set up scroll observer to maintain scroll position
    this.setupScrollObserver()

    // Set default mode to instruct
    this.switchMode('instruct')
    this.refreshSentiment()

    // Poll for new messages every 5 seconds
    this.pollInterval = setInterval(() => {
      this.pollMessages()
    }, 5000)
  }

  disconnect() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
    }
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
  }

  setupScrollObserver() {
    const messagesContainer = this.element.querySelector('[data-messages-container]')
    if (!messagesContainer) return

    // Track if user has scrolled up
    this.userHasScrolledUp = false

    messagesContainer.addEventListener('scroll', () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 50
      this.userHasScrolledUp = !isAtBottom
    })

    // Observe changes to messages container
    const messages = this.element.querySelector('#messages')
    if (messages) {
      this.scrollObserver = new MutationObserver(() => {
        // Auto-scroll only if user hasn't scrolled up
        if (!this.userHasScrolledUp) {
          this.scrollToBottom()
        }
      })

      this.scrollObserver.observe(messages, {
        childList: true,
        subtree: true
      })
    }
  }

  scrollToBottom(behavior = 'smooth') {
    const messagesContainer = this.element.querySelector('[data-messages-container]')
    if (messagesContainer) {
      console.log('Scrolling to bottom:', {
        scrollHeight: messagesContainer.scrollHeight,
        clientHeight: messagesContainer.clientHeight,
        behavior: behavior
      })

      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: behavior
      })
    } else {
      console.warn('Messages container not found')
    }
  }

  async pollMessages() {
    // TODO: Implement message polling via fetch
    // This would fetch new messages and update the view
  }

  async sendInstruction(event) {
    event.preventDefault()
    if (this.element.dataset.groupCommandUrl) return this.sendGroupIntervention("instruct", this.instructionInputTarget, event.currentTarget)

    const instruction = this.instructionInputTarget.value.trim()
    if (!instruction) {
      return
    }

    const url = this.element.dataset.instructionUrl
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = url

    const csrfToken = document.querySelector('[name="csrf-token"]').content
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken

    const instructionInput = document.createElement('input')
    instructionInput.type = 'hidden'
    instructionInput.name = 'instruction'
    instructionInput.value = instruction

    form.appendChild(csrfInput)
    form.appendChild(instructionInput)
    document.body.appendChild(form)

    // Submit via Turbo to get Turbo Stream response
    form.requestSubmit()
    form.remove()

    // Scroll to bottom after sending
    setTimeout(() => {
      this.scrollToBottom()
    }, 100)
  }

  showInstructModal(event) {
    event.preventDefault()
    this.switchMode('instruct')
    this.refreshSentiment()
  }

  showWhisperModal(event) {
    event.preventDefault()
    this.switchMode('whisper')
    this.refreshSentiment()
  }

  showSpeakModal(event) {
    event.preventDefault()
    this.switchMode('speak')
    this.refreshSentiment()
  }

  switchMode(mode) {
    // Hide all sections
    if (this.hasInstructSectionTarget) this.instructSectionTarget.classList.add('hidden')
    if (this.hasWhisperSectionTarget) this.whisperSectionTarget.classList.add('hidden')
    if (this.hasSpeakSectionTarget) this.speakSectionTarget.classList.add('hidden')

    // Reset all buttons to default state
    this.resetButtonStyles()

    // Show the selected section and highlight its button
    this.currentMode = mode
    switch(mode) {
      case 'instruct':
        if (this.hasInstructSectionTarget) {
          this.instructSectionTarget.classList.remove('hidden')
          this.instructSectionTarget.classList.add('flex')
          if (this.hasInstructionInputTarget) {
            this.instructionInputTarget.focus()
          }
        }
        if (this.hasInstructButtonTarget) {
          this.highlightButton(this.instructButtonTarget)
        }
        break
      case 'whisper':
        if (this.hasWhisperSectionTarget) {
          this.whisperSectionTarget.classList.remove('hidden')
          this.whisperSectionTarget.classList.add('flex')
          if (this.hasWhisperInputTarget) {
            this.whisperInputTarget.focus()
          }
        }
        if (this.hasWhisperButtonTarget) {
          this.highlightButton(this.whisperButtonTarget)
        }
        break
      case 'speak':
        if (this.hasSpeakSectionTarget) {
          this.speakSectionTarget.classList.remove('hidden')
          this.speakSectionTarget.classList.add('flex')
          if (this.hasSpeakInputTarget) {
            this.speakInputTarget.focus()
          }
        }
        if (this.hasSpeakButtonTarget) {
          this.highlightButton(this.speakButtonTarget)
        }
        break
    }
  }

  resetButtonStyles() {
    // Reset all buttons to inactive state (secondary_gray variant classes)
    const buttons = [
      { button: this.instructButtonTarget, classes: ['bg-[#ebebff]', 'text-[#7147ff]', 'border-[#7147ff]'] },
      { button: this.whisperButtonTarget, classes: ['bg-[#fff7ed]', 'text-[#fb923c]', 'border-[#fb923c]'] },
      { button: this.speakButtonTarget, classes: ['bg-[#f0fdf4]', 'text-[#22c55e]', 'border-[#22c55e]'] }
    ]

    buttons.forEach(({ button, classes }) => {
      if (button) {
        // Reset to secondary_gray variant (white bg, gray border and text)
        button.classList.remove(...classes)
        button.classList.add('bg-white', 'text-[#374151]', 'border-[#E4E5E8]')
      }
    })
  }

  highlightButton(button) {
    // Remove default styles
    button.classList.remove('bg-white', 'text-[#374151]', 'border-[#E4E5E8]')

    // Apply active state with color matching the input area
    if (button === this.instructButtonTarget) {
      // Purple theme for Instruct
      button.classList.add('bg-[#ebebff]', 'text-[#7147ff]', 'border-[#7147ff]')
    } else if (button === this.whisperButtonTarget) {
      // Orange theme for Whisper
      button.classList.add('bg-[#fff7ed]', 'text-[#fb923c]', 'border-[#fb923c]')
    } else if (button === this.speakButtonTarget) {
      // Green theme for Speak
      button.classList.add('bg-[#f0fdf4]', 'text-[#22c55e]', 'border-[#22c55e]')
    }
  }

  async sendWhisper(event) {
    event.preventDefault()
    if (this.element.dataset.groupCommandUrl) return this.sendGroupIntervention("whisper", this.whisperInputTarget, event.currentTarget)

    const whisper = this.whisperInputTarget.value.trim()
    if (!whisper) {
      return
    }

    const url = this.element.dataset.whisperUrl
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = url

    const csrfToken = document.querySelector('[name="csrf-token"]').content
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken

    const whisperInput = document.createElement('input')
    whisperInput.type = 'hidden'
    whisperInput.name = 'whisper'
    whisperInput.value = whisper

    form.appendChild(csrfInput)
    form.appendChild(whisperInput)
    document.body.appendChild(form)

    // Submit via Turbo to get Turbo Stream response
    form.requestSubmit()
    form.remove()

    // Scroll to bottom after sending
    setTimeout(() => {
      this.scrollToBottom()
    }, 100)
  }

  async sendSpeak(event) {
    event.preventDefault()
    if (this.element.dataset.groupCommandUrl) return this.sendGroupIntervention("speak", this.speakInputTarget, event.currentTarget)
    if (this.sendingSpeak) return
    const input = this.speakInputTarget
    const draft = input.value
    if (!draft.trim()) return
    const button = event.currentTarget
    const status = this.speakStatusTarget
    this.sendingSpeak = true
    button.disabled = true
    status.textContent = "Translating and sending…"
    try {
      const response = await fetch(this.element.dataset.speakUrl, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ speak: draft })
      })
      const data = await response.json()
      if (!response.ok || response.redirected) throw new Error(data.error || "Could not send your message. Please try again.")
      // Preserve any new text written while this message was translating.
      if (input.value === draft) input.value = ""
      status.textContent = "Sent in the participant's language."
      this.scrollToBottom()
    } catch (error) {
      status.textContent = error.message || "Could not send your message. Please try again."
    } finally {
      this.sendingSpeak = false
      button.disabled = false
    }
  }

  async sendGroupIntervention(operation, input, button) {
    const content = input.value
    if (!content.trim() || this.sendingGroupIntervention || button.disabled) return
    this.sendingGroupIntervention = true
    button.disabled = true
    const status = this.element.querySelector("[data-group-intervention-status]")
    status.textContent = "Sending…"
    try {
      const response = await fetch(this.element.dataset.groupCommandUrl, {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ group_id: this.element.dataset.groupId, operation, content })
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Unable to send. Please try again.")
      }
      if (input.value === content) input.value = ""
      status.textContent = ""
      this.element.dispatchEvent(new CustomEvent("group-intervention:sent", { bubbles: true }))
      this.scrollToBottom()
    } catch (error) { status.textContent = error.message }
    finally { this.sendingGroupIntervention = false; button.disabled = input.disabled }
  }

  async refreshSentiment() {
    const url = this.element.dataset.sentimentUrl
    if (!url || !this.hasSentimentBannerTarget || this.refreshingSentiment) return

    this.refreshingSentiment = true
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      this.renderSentiment(response.ok ? await response.json() : { available: false })
    } catch (error) {
      console.warn('Sentiment check failed', error)
      this.renderSentiment({ available: false })
    } finally {
      this.refreshingSentiment = false
    }
  }

  renderSentiment(data) {
    const banner = this.sentimentBannerTarget

    if (!data || !data.available) {
      banner.classList.remove('hidden', 'border-[#FCA5A5]', 'bg-[#FEF2F2]')
      banner.classList.add('border-[#E4E5E8]', 'bg-[#F9FAFB]')
      this.sentimentDotTarget.classList.remove('bg-[#EF4444]')
      this.sentimentDotTarget.classList.add('bg-[#9CA3AF]')
      this.sentimentHeadlineTarget.textContent = 'Participant sentiment'
      this.sentimentDetailTarget.textContent = 'Sentiment is unavailable right now. Select Instruct, Whisper or Speak to try again.'
      return
    }

    const warning = Boolean(data.warning)
    const negativePercent = Math.round((data.negative_probability || 0) * 100)
    const frustrationPercent = Math.round((data.frustration_probability || 0) * 100)
    const label = String(data.label || '').replace(/_/g, ' ')

    this.sentimentHeadlineTarget.textContent = warning
      ? 'Participant sentiment needs attention'
      : 'Participant sentiment'
    this.sentimentDetailTarget.textContent = label + ' (' + negativePercent + '% negative, ' + frustrationPercent + '% frustration) - ' + (data.suggestion || '')

    banner.classList.remove('hidden')
    banner.classList.remove('border-[#E4E5E8]', 'border-[#FCA5A5]', 'bg-[#F9FAFB]', 'bg-[#FEF2F2]')
    banner.classList.add(warning ? 'border-[#FCA5A5]' : 'border-[#E4E5E8]')
    banner.classList.add(warning ? 'bg-[#FEF2F2]' : 'bg-[#F9FAFB]')

    this.sentimentDotTarget.classList.remove('bg-[#9CA3AF]', 'bg-[#EF4444]')
    this.sentimentDotTarget.classList.add(warning ? 'bg-[#EF4444]' : 'bg-[#9CA3AF]')
  }

}
